"use client";

import { Box, styled, Typography } from "@mui/material";
import Image from "next/image";
import RogerAvatar from "./roger-avatar.jpg";
import { Article } from "../../articles/Article";
import { HUB_BREADCRUMBS, HubBreadcrumbs } from "../../HubBreadcrumbs";
import { ShareButton } from "../../ShareButton";

const Container = styled(Box)({
  maxWidth: "800px",
  margin: "0 auto",
});

function ArticleMeta({ article }: { article: Article }) {
  const avatarSize = 50;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `${avatarSize}px 1fr auto`,
        gridTemplateRows: "auto auto",
        alignItems: "center",
        columnGap: 2,
      }}
    >
      <Box
        component={Image}
        src={RogerAvatar}
        alt="Roger Avatar"
        width={avatarSize}
        height={avatarSize}
        sx={{
          borderRadius: "50%",
          gridRow: "span 2",
        }}
      />
      <Typography
        sx={{
          gridRow: 1,
          gridColumn: 2,
        }}
      >
        By <strong>Roger Fitzhardinge</strong>
      </Typography>
      <Typography
        sx={{
          gridRow: 2,
          gridColumn: 2,
        }}
        variant="body2"
      >
        {article.createdAt}
      </Typography>
      <Box
        sx={{
          gridRow: "1 /span 2",
          gridColumn: 3,
        }}
      >
        <ShareButton
          item={{
            id: article.slug,
            title: article.title,
            url: `/hub/guides/${article.slug}`,
            descriptionShort: article.preview,
          }}
        />
      </Box>
    </Box>
  );
}

export function HubArticlePage({ article }: { article: Article }) {
  return (
    <Box
      sx={(theme) => ({
        backgroundColor: "grey.200",
        height: "100%",
        paddingBottom: 8,
        [theme.breakpoints.down("sm")]: {
          paddingBottom: 0,
          backgroundColor: "white",
        },
      })}
    >
      <Container
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          paddingTop: 2,
          paddingBottom: 2,
        }}
      >
        <HubBreadcrumbs
          breadcrumbs={[
            HUB_BREADCRUMBS.hub,
            HUB_BREADCRUMBS.guides,
            {
              label: article.title,
              url: `/hub/guides/${article.slug}`,
            },
          ]}
        />
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            gap: 4,
            padding: 4,
            backgroundColor: "white",
            borderRadius: 10,
            [theme.breakpoints.down("sm")]: {
              padding: 2,
              borderRadius: 0,
            },
          })}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography
                variant="caption"
                sx={(theme) => ({
                  backgroundColor: "grey.100",
                  alignSelf: "flex-start",
                  padding: theme.spacing(0.5, 1),
                  borderRadius: 2,
                  fontWeight: "bold",
                })}
              >
                GUIDES
              </Typography>
              <Typography variant="h4" component="h1">
                {article.title}
              </Typography>
            </Box>
            <ArticleMeta article={article} />
          </Box>
          <Box
            sx={(theme) => ({
              display: "flex",
              flexDirection: "column",
              gap: 2,
              p: {
                ...theme.typography.body1,
              },
              h1: {
                ...theme.typography.h4,
              },
              "ol, ul": {
                display: "flex",
                flexDirection: "column",
                gap: 1,
                paddingLeft: 2,
                li: {
                  paddingLeft: 1,
                },
              },
            })}
            dangerouslySetInnerHTML={{ __html: article.bodyHtml }}
          />
          <Box
            sx={{
              alignSelf: "flex-end",
            }}
          >
            <ShareButton
              item={{
                id: article.slug,
                title: article.title,
                url: `/hub/guides/${article.slug}`,
                descriptionShort: article.preview,
              }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
